import { ChevronRight } from "lucide-react";
import posthog from "posthog-js";
import { Link, useNavigate } from "react-router-dom";

import { slugifyURL } from "@/app/handlers/urlFunctions";
import JobListingItem from "@/app/Views/JobsPage/components/JobListingItem";
import { RoleExplainerSurface } from "../hooks/useJobPageAnalytics";
import { SimilarJob, useSimilarJobs } from "../hooks/useSimilarJobs";
import { JobDetails } from "../utils/helpers";
import { SimilarRoleCard } from "./SimilarRoleCard";

/** Roles listed inline. Past this the section defers to the category browse for the rest. */
const MAX_LISTED_ROLES = 3;

interface SimilarRolesProps {
	jobDetails: JobDetails;
	/** Decides the shape: cards on the standalone page, rows in the drawer. */
	surface: RoleExplainerSurface;
}

/**
 * Open roles in the same category, from other employers.
 *
 * This used to be a bare "explore more roles" link, which asked the reader to start a search
 * rather than showing them what the search would return. Listing a few real roles lets someone
 * who has decided against this posting keep going without leaving the page.
 */
export function SimilarRoles({ jobDetails, surface }: SimilarRolesProps) {
	const navigate = useNavigate();

	const category = jobDetails?.jobExpertise;
	const { roles } = useSimilarJobs({
		category,
		currentJobId: jobDetails?._id,
		companyId: jobDetails?.company?._id,
	});

	// Nothing worth a heading when this is the only live role in its category.
	if (roles.length === 0) return null;

	const listed = roles.slice(0, MAX_LISTED_ROLES);
	const viewAllHref = `/jobs${category ? `?category=${encodeURIComponent(category)}` : ""}`;

	const trackRoleClick = (job: SimilarJob, position: number) => {
		posthog.capture("similar_role_clicked", {
			jobId: job._id,
			jobTitle: job.jobTitle,
			companyId: job.company?._id,
			companyName: job.company?.company_name,
			sourceJobId: jobDetails?._id,
			category,
			position,
			surface,
		});
	};

	// The title of each row and card is already a link, so only the CTA has to navigate itself.
	const openRole = (job: SimilarJob, position: number) => {
		trackRoleClick(job, position);

		const companyName = job.company?.company_name || job.companyName;
		if (!job._id || !job.jobTitle || !companyName) return;

		navigate(`/jobs/${slugifyURL({ _id: job._id, companyName, jobTitle: job.jobTitle })}`);
		window.scrollTo(0, 0);
	};

	const openViewAll = () => {
		posthog.capture("similar_roles_view_all_clicked", {
			sourceJobId: jobDetails?._id,
			category,
			listedCount: listed.length,
			surface,
		});

		// This section sits at the foot of a long page, and the router keeps the scroll offset
		// across the navigation — without this the browse opens somewhere down in its footer.
		window.scrollTo(0, 0);
	};

	return (
		<section className="mt-8 mb-8">
			<div className="flex items-center justify-between gap-4 py-3 border-b border-gray-200">
				{/* min-w-0 so the heading column gives way to the CTA rather than pushing it off. */}
				<div className="min-w-0">
					<h2 className="text-2xl font-semibold font-inter text-gray-900">Similar roles</h2>
					<p className="text-sm text-gray-500 hidden sm:block">
						Open {category ? `${category.toLowerCase()} ` : ""}roles at other employers.
					</p>
				</div>

				<Link
					to={viewAllHref}
					onClick={openViewAll}
					className="shrink-0 inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium"
				>
					View all
					<ChevronRight className="h-4 w-4" />
				</Link>
			</div>

			{surface === "job_details_page" ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
					{listed.map((job, index) => (
						<SimilarRoleCard
							key={job._id}
							job={job}
							onSelect={() => trackRoleClick(job, index)}
							onOpen={() => openRole(job, index)}
						/>
					))}
				</div>
			) : (
				<div className="divide-y divide-gray-200">
					{listed.map((job, index) => (
						<JobListingItem
							key={job._id}
							job={job}
							// These span employers, unlike the company's own roles, so each row has to say whose it is.
							showCompany={true}
							titleClassName="text-sm font-semibold"
							onClick={() => trackRoleClick(job, index)}
							onApply={() => openRole(job, index)}
						/>
					))}
				</div>
			)}
		</section>
	);
}
